import React, {ReactElement, ReactText, useEffect, useState} from 'react';
import {useInView} from 'react-intersection-observer';
import {useIntl} from 'react-intl';
import {getIsIntersectionObserverSupported} from '../../../browserSupport';

import {useLocale} from '../../../RootProvider/LocaleContext';

export type AnimatedValueTemplate = (s: ReactText) => string;

interface Props {
  value: number;
  template?: AnimatedValueTemplate;
  startAt?: number;
  duration?: number;
  tickSpeed?: number;
  className?: string;
}

function AnimatedNumber({
  value,
  template,
  startAt = 1,
  duration = 1000,
  tickSpeed = 30,
  className,
}: Props): ReactElement {
  const locale = useLocale();
  const {formatMessage} = useIntl();
  const [_animateValue, setAnimateValue] = useState(startAt);
  const [displayValue, setDisplayValue] = useState<string>('');
  const [intervalRef, setIntervalRef] = useState<number>();
  const isSupported = getIsIntersectionObserverSupported();

  // Using a hook in a conditional is an anti-pattern, as it can affect the order in which the hooks are called (see https://reactjs.org/docs/hooks-rules.html#only-call-hooks-at-the-top-level)
  // However, running the useInView hook without IntersectionObserver in the window will cause errors
  // Since the value of isSupported is _not_ dynamic and is impossible to change either in server or on browser, this will not effect the order of the hook calls
  const [ref, isInView] = isSupported
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useInView({
        threshold: 0.4, // trigger when 40% of number is in view, only trigger for one view then tear down observer
        triggerOnce: true,
      })
    : [null, true];

  if (!template) {
    // must set default here so intl context is available
    // eslint-disable-next-line no-param-reassign
    template = (s: ReactText) => formatMessage({id: 'homepage.businessStats.customers.stat.template'}, {stat: s});
  }

  // calculate animation params
  const numTicks = duration / tickSpeed;
  const increment = Math.max((value - startAt) / numTicks, 1); // maxed to prevent tick value of 0

  const count = () => {
    // increment by value using callback set state, this ensures we increment the currently displayed value
    setAnimateValue((currentValue) => {
      if (currentValue < value) {
        // math.min to prevent ticking past intended value
        const nextVal = Math.round(Math.min(currentValue + increment, value));
        setDisplayValue(nextVal.toLocaleString(locale));

        // teardown interval
        if (nextVal === value) {
          setIntervalRef((currentInterval) => {
            if (currentInterval) {
              clearInterval(currentInterval);
            }
            return undefined;
          });
        }
        return nextVal;
      }

      return currentValue;
    });
  };

  // trigger ticker when component comes in view (browser only)
  useEffect(() => {
    if (isSupported && isInView) {
      const interval = window.setInterval(count, tickSpeed);
      setIntervalRef(interval);
    }
    return () => {
      if (intervalRef) {
        clearInterval(intervalRef);
        setIntervalRef(undefined);
      }
    };
  }, [isInView, isSupported]);

  return (
    <span className={className} ref={ref}>
      {isSupported ? template(displayValue || startAt) : template(value)}
    </span>
  );
}

export default AnimatedNumber;
